/* PureTitan advertorial — accordions, countdowns, video cards, reveal, mobile sticky CTA.
   Safe to load once per page; re-initialises scoped to a section on theme-editor reloads. */
(() => {
  'use strict';
  if (window.PTA) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Accordions: native <details>, animated height ---------- */
  function initAccordions(root) {
    root.querySelectorAll('details[data-pta-acc]').forEach((d) => {
      if (d.dataset.ptaBound) return;
      d.dataset.ptaBound = '1';
      const summary = d.querySelector(':scope > summary');
      const panel = d.querySelector(':scope > .pta-acc__panel');
      if (!summary || !panel || reduce) return;
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        if (d.dataset.animating) return;
        d.dataset.animating = '1';
        if (d.open) {
          const h = panel.offsetHeight;
          panel.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 450, easing: 'ease' })
            .onfinish = () => { d.open = false; delete d.dataset.animating; };
        } else {
          d.open = true;
          const h = panel.offsetHeight;
          panel.animate([{ height: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }], { duration: 450, easing: 'ease' })
            .onfinish = () => { delete d.dataset.animating; };
        }
      });
    });
  }

  /* ---------- Countdown to a fixed deadline (no resets) ---------- */
  const timers = new Map();
  function pad(n) { return String(n).padStart(2, '0'); }
  /* Loop mode: end of the current cycle on a fixed clock (same for everyone, survives refresh). */
  function loopEnd(el) {
    const cycle = (parseInt(el.dataset.ptaLoop, 10) || 24) * 3600000;
    const hour = parseInt(el.dataset.ptaLoopHour, 10) || 0;
    const m = /^([+-])(\d{2}):(\d{2})$/.exec(el.dataset.ptaTz || '+03:00');
    const off = m ? (m[1] === '-' ? -1 : 1) * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10)) * 60000 : 0;
    const anchor = Date.UTC(2020, 0, 1, hour) - off;
    const now = Date.now();
    return anchor + (Math.floor((now - anchor) / cycle) + 1) * cycle;
  }
  function initCountdowns(root) {
    root.querySelectorAll('[data-pta-deadline], [data-pta-loop]').forEach((el) => {
      if (timers.has(el)) return;
      const isLoop = el.hasAttribute('data-pta-loop');
      let end = isLoop ? loopEnd(el) : Date.parse(el.dataset.ptaDeadline);
      if (Number.isNaN(end)) { el.hidden = true; return; }
      const scope = el.closest('[data-pta-cd-scope]') || el.parentElement;
      const expired = scope && scope.querySelector('[data-pta-expired]');
      const mode = el.dataset.ptaExpire || 'hide';
      const hEl = el.querySelector('[data-h]'), mEl = el.querySelector('[data-m]'), sEl = el.querySelector('[data-s]');
      const tick = () => {
        if (isLoop && end - Date.now() <= 0) end = loopEnd(el);
        const left = Math.max(0, Math.floor((end - Date.now()) / 1000));
        if (left <= 0) {
          clearInterval(timers.get(el));
          el.hidden = true;
          if (expired && mode === 'message') expired.hidden = false;
          if (mode === 'hide_section' && scope) scope.closest('.shopify-section')?.setAttribute('hidden', '');
          return;
        }
        const h = Math.floor(left / 3600), m = Math.floor((left % 3600) / 60), s = left % 60;
        if (hEl) hEl.textContent = pad(h);
        if (mEl) mEl.textContent = pad(m);
        if (sEl) sEl.textContent = pad(s);
        el.hidden = false;
      };
      tick();
      timers.set(el, setInterval(tick, 1000));
    });
  }
  function clearCountdowns(root) {
    for (const [el, id] of timers) { if (root.contains(el)) { clearInterval(id); timers.delete(el); } }
  }

  /* ---------- Video cards: click to play inline ---------- */
  function initVideos(root) {
    root.querySelectorAll('[data-pta-play]').forEach((btn) => {
      if (btn.dataset.ptaBound) return;
      btn.dataset.ptaBound = '1';
      btn.addEventListener('click', () => {
        const box = btn.closest('.pta-vs__media');
        const tpl = box && box.querySelector('template');
        if (!tpl) return;
        box.querySelectorAll('img').forEach((i) => i.remove());
        box.appendChild(tpl.content.cloneNode(true));
        btn.remove();
        const v = box.querySelector('video');
        if (v) { v.play().catch(() => {}); v.focus(); }
      });
    });
    /* drag to scroll on desktop */
    root.querySelectorAll('.pta-vs__track').forEach((track) => {
      if (track.dataset.ptaBound) return;
      track.dataset.ptaBound = '1';
      let down = false, startX = 0, startScroll = 0, moved = false;
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        down = true; moved = false; startX = e.clientX; startScroll = track.scrollLeft;
      });
      track.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 4) { moved = true; track.classList.add('is-dragging'); }
        track.scrollLeft = startScroll - dx;
      });
      const up = () => { down = false; track.classList.remove('is-dragging'); };
      track.addEventListener('pointerup', up);
      track.addEventListener('pointerleave', up);
      track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
    });
  }

  /* ---------- Reveal on scroll ---------- */
  let revealIO = null;
  function initReveal(root) {
    const els = root.querySelectorAll('.pta-reveal:not(.is-in)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window) || reduce) { els.forEach((e) => e.classList.add('is-in')); return; }
    revealIO = revealIO || new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); revealIO.unobserve(en.target); } });
    }, { threshold: 0.15 });
    els.forEach((e) => revealIO.observe(e));
  }

  /* ---------- Mobile sticky CTA ---------- */
  let stickyState = null;
  function initSticky() {
    const bar = document.querySelector('[data-pta-sticky]');
    if (stickyState) { stickyState.io.disconnect(); window.removeEventListener('scroll', stickyState.onScroll); stickyState = null; }
    document.body.classList.remove('pta-sticky-on');
    if (!bar) return;
    const hero = document.querySelector('[data-pta-hero]');
    const hideNear = Array.from(document.querySelectorAll('[data-pta-hide-sticky]'));
    const vis = new Map();
    const update = () => {
      const pastHero = hero ? hero.getBoundingClientRect().bottom < 0 : window.scrollY > 400;
      const nearHide = Array.from(vis.values()).some(Boolean);
      const show = pastHero && !nearHide;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      bar.querySelectorAll('a,button').forEach((a) => { a.tabIndex = show ? 0 : -1; });
      document.body.classList.toggle('pta-sticky-on', show);
      if (show) document.documentElement.style.setProperty('--pta-sticky-offset', bar.offsetHeight + 'px');
    };
    const io = new IntersectionObserver((entries) => { entries.forEach((en) => vis.set(en.target, en.isIntersecting)); update(); });
    hideNear.forEach((el) => io.observe(el));
    let raf = 0;
    const onScroll = () => { if (raf) return; raf = requestAnimationFrame(() => { raf = 0; update(); }); };
    window.addEventListener('scroll', onScroll, { passive: true });
    stickyState = { io, onScroll };
    update();
  }

  function init(root = document) {
    initAccordions(root);
    initCountdowns(root);
    initVideos(root);
    initReveal(root);
  }

  window.PTA = { init };
  const boot = () => { init(document); initSticky(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (e) => { init(e.target); initSticky(); });
  document.addEventListener('shopify:section:unload', (e) => { clearCountdowns(e.target); setTimeout(initSticky, 0); });
  document.addEventListener('shopify:block:select', (e) => {
    const d = e.target.closest('details[data-pta-acc]') || e.target.querySelector?.('details[data-pta-acc]');
    if (d) d.open = true;
  });
})();